import { masters } from "@nexseed/shared";
import { Package } from "lucide-react";
import { defineMaster, type MasterRow } from "../spec";

/**
 * How a crop's pouches of one size are boxed: pouches per window box (WB), WB weight, WBs per master
 * pack (MP), MP weight. The Packing Slip, the master-pack slips and the handheld scan loop all read
 * it (the legacy app's nopinwb / wbwt / wbinmp / wtmp). ST sizes must fill a WB exactly; NST
 * (non-standard) sizes take a typed MP weight on the slip. docs/designs/packaging-module.md, P1.
 * A row with a Variety overrides its crop's row of the same size for that variety only (0184).
 */
const UPS_TYPE_LABELS: Record<(typeof masters.UPS_TYPES)[number], string> = {
  ST: "Standard (ST)",
  NST: "Non-standard (NST)",
};
const UPS_TYPES = masters.UPS_TYPES.map((t) => ({
  value: t,
  label: UPS_TYPE_LABELS[t],
}));

const kgCell = (v: unknown) =>
  v === null || v === undefined || v === "" ? "" : Number(v).toFixed(3);

export const packLayouts = defineMaster({
  key: "pack-layouts",
  title: "Pack Layouts",
  singular: "Pack Layout",
  legacyName:
    "Pack details on the WB Packing Slip (NoP in WB, WB Weight, WB in MP, MP Weight)",
  description:
    "How each crop's pouches of one size go into window boxes (WB) and master packs (MP). The packing slips and the handheld read it.",
  icon: Package,
  schema: masters.packLayout.create,
  searchPlaceholder: "Search crop or pack size",
  defaultSort: "cropId",
  columns: [
    {
      id: "cropId",
      header: "Crop",
      kind: "reference",
      reference: "crops",
      sortable: true,
    },
    {
      id: "varietyId",
      header: "Variety",
      kind: "reference",
      reference: "varieties",
    },
    {
      id: "packSizeId",
      header: "UPS",
      kind: "reference",
      reference: "processing-resources",
    },
    { id: "upstype", header: "Type", kind: "mono" },
    {
      id: "pouchesInWb",
      header: "Pouches in WB",
      kind: "number",
      align: "right",
    },
    {
      id: "wbWeightKg",
      header: "WB Weight (kg)",
      align: "right",
      cell: (r: MasterRow) => kgCell(r.wbWeightKg),
    },
    { id: "wbInMp", header: "WBs in MP", kind: "number", align: "right" },
    {
      id: "mpWeightKg",
      header: "MP Weight (kg)",
      align: "right",
      cell: (r: MasterRow) => kgCell(r.mpWeightKg),
    },
    {
      id: "pouchesInMp",
      header: "Pouches in MP",
      align: "right",
      cell: (r: MasterRow) =>
        Number(r.pouchesInWb) > 0 && Number(r.wbInMp) > 0
          ? String(Number(r.pouchesInWb) * Number(r.wbInMp))
          : "",
    },
    { id: "maxMp", header: "Max MPs", kind: "number", align: "right" },
    { id: "isActive", header: "Status", kind: "status" },
  ],
  fields: [
    {
      name: "cropId",
      label: "Crop",
      type: "reference",
      master: "crops",
      required: true,
    },
    {
      name: "varietyId",
      label: "Variety (only for a variety boxed differently)",
      type: "reference",
      master: "varieties",
      dependsOn: { field: "cropId", param: "cropId" },
      description:
        "Leave blank for the crop's layout. Pick a variety to override it for that variety only",
    },
    {
      name: "packSizeId",
      label: "Pack Size (UPS)",
      type: "reference",
      master: "processing-resources",
      lookupFilter: { kind: "pack_size" },
      required: true,
    },
    {
      name: "upstype",
      label: "Pack Type",
      type: "enum",
      required: true,
      options: UPS_TYPES,
      description:
        "ST: the pouches typed on a slip must fill whole window boxes. NST: the slip takes a typed MP weight",
      colSpan: 2,
    },
    {
      name: "pouchesInWb",
      label: "Pouches in one WB",
      type: "number",
      step: 1,
      min: 1,
      required: true,
    },
    {
      name: "wbWeightKg",
      label: "WB Weight (kg)",
      type: "number",
      step: 0.001,
      min: 0,
      required: true,
    },
    {
      name: "wbInMp",
      label: "WBs in one MP",
      type: "number",
      step: 1,
      min: 1,
      required: true,
    },
    {
      name: "mpWeightKg",
      label: "MP Weight (kg)",
      type: "number",
      step: 0.001,
      min: 0,
      required: true,
      description: "Net weight of a full master pack",
    },
    {
      name: "maxMp",
      label: "Max MPs per slip",
      type: "number",
      step: 1,
      min: 1,
      description: "Leave blank for no limit",
    },
    { name: "isActive", label: "Active", type: "switch" },
  ],
});

/**
 * Master pack types (D22): what an NST master pack is packed in (a carton or a bag) and its tare, per
 * plant. The NST Packing Slip types only the MP's GROSS weight; pouches per MP =
 * floor((gross − tare) ÷ UPS) in whole grams, with the tare from the row's MP Type here. API:
 * GET/POST/PATCH /masters/mp-types (table `mp_types`, migration 0108), schema in @nexseed/shared.
 */
const MP_TYPE_KINDS = [
  { value: "carton", label: "Carton" },
  { value: "bag", label: "Bag" },
];

export const mpTypes = defineMaster({
  key: "mp-types",
  title: "MP Types",
  singular: "MP Type",
  legacyName: "NST Packing Slip MP Weight",
  description:
    "What a master pack is packed in, carton or bag, and what the empty pack weighs. The NST Packing Slip takes the tare off the typed gross MP weight.",
  icon: Package,
  schema: masters.mpType.create,
  searchPlaceholder: "Search code or name",
  defaultSort: "code",
  columns: [
    { id: "code", header: "Code", kind: "mono", sortable: true },
    { id: "name", header: "Name", sortable: true },
    {
      id: "mpType",
      header: "MP Type",
      cell: (r: MasterRow) =>
        MP_TYPE_KINDS.find((k) => k.value === r.mpType)?.label ?? "",
    },
    {
      id: "tareKg",
      header: "Tare (kg)",
      align: "right",
      cell: (r: MasterRow) => kgCell(r.tareKg),
    },
    {
      id: "facilityId",
      header: "Plant",
      kind: "reference",
      reference: "facilities",
    },
    { id: "isActive", header: "Status", kind: "status" },
  ],
  fields: [
    {
      name: "code",
      label: "Code",
      type: "text",
      transform: "upper",
      required: true,
      description: "Unique at the plant, e.g. CTN5",
    },
    {
      name: "name",
      label: "Name",
      type: "text",
      required: true,
      description: "e.g. Carton 5 kg, Bag 25 kg",
    },
    {
      name: "mpType",
      label: "MP Type",
      type: "enum",
      required: true,
      options: MP_TYPE_KINDS,
    },
    {
      name: "tareKg",
      label: "Tare (kg)",
      type: "number",
      step: 0.001,
      min: 0,
      required: true,
      description:
        "What the empty carton or bag weighs, 3 decimals. Taken off the gross MP weight on the slip",
    },
    {
      name: "facilityId",
      label: "Plant",
      type: "reference",
      master: "facilities",
      required: true,
      colSpan: 2,
    },
    { name: "isActive", label: "Active", type: "switch" },
  ],
});
